import { AnimatePresence, m } from "framer-motion";
import React, { useState, useEffect } from "react";
// @mui
import { alpha, styled } from "@mui/material/styles";
import {
  Stack,
  Divider,
  Backdrop,
  Typography,
  IconButton,
  Box, // Import Box for flexible layout
} from "@mui/material";
// hooks
import useSettings from "../../../hooks/useSettings";
// utils
import cssStyles from "../../../utils/cssStyles";
// config
import { defaultSettings } from "../../../config"; // Removed NAVBAR import as width is now dynamic
//
import Iconify from "../../Iconify";
import Scrollbar from "../../Scrollbar";
//
import ToggleButton from "./ToggleButton";
import PatientInformation from "../../PatientInformation"; // Corrected import path

// ----------------------------------------------------------------------

const RootStyle = styled(m.div)(({ theme }) => ({
  ...cssStyles(theme).bgBlur({
    color: theme.palette.background.paper,
    opacity: 0.92,
  }),
  top: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  position: "fixed",
  overflow: "hidden",
  // Responsive width: full width on xs, 320px on sm and up
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 320,
  },
  flexDirection: "column",
  margin: theme.spacing(2),
  paddingBottom: theme.spacing(3),
  zIndex: theme.zIndex.drawer + 3,
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  boxShadow: `-24px 12px 32px -4px ${alpha(
    theme.palette.mode === "light"
      ? theme.palette.grey[500]
      : theme.palette.common.black,
    0.16
  )}`,
}));

// ----------------------------------------------------------------------

export default function SettingsDrawer({ isChatPage }) {
  const {
    themeMode,
    themeLayout,
    themeStretch,
    themeContrast,
    themeDirection,
    themeColorPresets,
    onResetSetting,
  } = useSettings();

  const [open, setOpen] = useState(false);

  const notDefault =
    themeMode !== defaultSettings.themeMode ||
    themeLayout !== defaultSettings.themeLayout ||
    themeStretch !== defaultSettings.themeStretch ||
    themeContrast !== defaultSettings.themeContrast ||
    themeDirection !== defaultSettings.themeDirection ||
    themeColorPresets !== defaultSettings.themeColorPresets;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Backdrop
        open={open}
        onClick={handleClose}
        sx={{
          background: "transparent",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      />

      {!open && isChatPage && ( // Render ToggleButton only if not open AND IS on chat page
        <ToggleButton
          open={open}
          notDefault={notDefault}
          onToggle={handleToggle}
        />
      )}

      <AnimatePresence>
        {open && (
          <RootStyle>
            {isChatPage ? (
              // If on chat page, render PatientInformation directly
              <PatientInformation onClose={handleClose} />
            ) : (
              // Otherwise, render the generic settings drawer content
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ py: 2, pr: 1, pl: 2.5 }}
                >
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                    Settings
                  </Typography>

                  <IconButton onClick={onResetSetting}>
                    <Iconify icon={"ic:round-refresh"} width={20} height={20} />
                  </IconButton>

                  <IconButton onClick={handleClose}>
                    <Iconify icon={"eva:close-fill"} width={20} height={20} />
                  </IconButton>
                </Stack>

                <Divider sx={{ borderStyle: "dashed" }} />

                <Scrollbar sx={{ flexGrow: 1}}>
                  <Stack spacing={3} sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      General App Settings
                    </Typography>
                  </Stack>
                </Scrollbar>
              </>
            )}
          </RootStyle>
        )}
      </AnimatePresence>
    </>
  );
}